<script setup>
import { computed } from 'vue'

import DayChart from '@/components/dashboards/DayChart.vue'

const props = defineProps({
  report: { type: Object, required: true },
})
const emit = defineEmits(['openRatings'])

// Validated for colour-blind readers and for contrast on the light surface
const COLOR = { blue: '#0588f0', teal: '#008573', amber: '#ab6400', ruby: '#e54666' }
const GROUPS = ['bot', 'bot_then_operator', 'operator']

const totals = computed(() => props.report.totals)
const days = computed(() => props.report.days)
const groups = computed(() => GROUPS.map(key => ({ key, ...props.report.groups[key] })))
const answeredPoints = computed(() => days.value.map(d => ({ day: d.day, value: d.answered })))
const botPoints = computed(() => days.value.map(d => ({ day: d.day, value: d.bot_avg })))
const operatorPoints = computed(() => days.value.map(d => ({ day: d.day, value: d.operator_avg })))
const histogram = computed(() =>
  [1, 2, 3, 4, 5].map(mark => ({ mark, count: totals.value.hist?.[mark] ?? 0 }))
)
const histMax = computed(() => Math.max(1, ...histogram.value.map(h => h.count)))
const reasons = computed(() => Object.entries(totals.value.reasons || {}))
const reasonsTotal = computed(() => Math.max(1, ...reasons.value.map(([, count]) => count)))
const pct = (part, whole) => (whole ? Math.round((part * 100) / whole) : 0)
const fmt = n => (n == null ? '—' : new Intl.NumberFormat('ru-RU').format(n))
const fmtAvg = v => (v == null ? '—' : v.toFixed(1).replace('.', ','))
const fmtShare = v => (v == null ? '—' : `${v} %`)
// CSI thresholds: 80 and up is good, under 60 is a problem
const csiTone = v => {
  if (v == null) return 'text-n-slate-11'
  if (v >= 80) return 'text-n-teal-11'
  return v >= 60 ? 'text-n-slate-12' : 'text-n-ruby-11'
}
const markColor = mark => (mark <= 2 ? COLOR.ruby : mark === 3 ? COLOR.amber : COLOR.teal)
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.ASKED') }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ fmt(totals.asked) }}</span>
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.ASKED_SUB') }}</span>
      </div>
      <button
        type="button"
        class="flex flex-col items-start gap-1 p-4 text-left rounded-xl border border-n-weak bg-n-solid-1 hover:border-n-blue-9"
        :disabled="!totals.answered"
        @click="emit('openRatings')"
      >
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.ANSWERED') }}</span>
        <span class="text-2xl font-semibold" :class="totals.answered ? 'text-n-blue-11' : 'text-n-slate-12'">
          {{ fmt(totals.answered) }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ $t('DASHBOARDS.CLIENTS.ANSWERED_SUB', { share: fmtShare(totals.answered_share) }) }}
          <template v-if="totals.answered"> · {{ $t('DASHBOARDS.CLIENTS.OPEN_LIST') }}</template>
        </span>
      </button>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.GOOD') }}</span>
        <span class="text-2xl font-semibold text-n-teal-11">{{ fmtShare(totals.good_share) }}</span>
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.GOOD_SUB', { count: fmt(totals.good) }) }}</span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.BOT_AVG') }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ fmtAvg(totals.bot_avg) }}
          <span
            class="text-base font-medium"
            :class="csiTone(totals.bot_csi)"
            :title="$t('DASHBOARDS.CSI_HINT')"
          >
            · CSI {{ fmt(totals.bot_csi) }}
          </span>
        </span>
        <span class="text-xs text-n-slate-11">
          {{ $t('DASHBOARDS.CLIENTS.AVG_SUB', { count: fmt(report.groups.bot.answered) }) }}
        </span>
      </div>
      <div class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.OPERATOR_AVG') }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ fmtAvg(totals.operator_avg) }}
          <span
            class="text-base font-medium"
            :class="csiTone(totals.operator_csi)"
            :title="$t('DASHBOARDS.CSI_HINT')"
          >
            · CSI {{ fmt(totals.operator_csi) }}
          </span>
        </span>
        <span class="text-xs text-n-slate-11">
          {{ $t('DASHBOARDS.CLIENTS.AVG_SUB', { count: fmt(report.groups.operator.answered) }) }}
        </span>
      </div>
    </div>

    <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
      <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.CLIENTS.GROUPS') }}</h2>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-xs text-n-slate-11">
            <th class="pb-1 font-normal text-left">{{ $t('DASHBOARDS.CLIENTS.GROUP') }}</th>
            <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.CLIENTS.COL_ASKED') }}</th>
            <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.CLIENTS.COL_ANSWERED') }}</th>
            <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.CLIENTS.COL_SHARE') }}</th>
            <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.CLIENTS.COL_AVG') }}</th>
            <th class="pb-1 font-normal text-right">CSI</th>
            <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.CLIENTS.COL_GOOD') }}</th>
            <th class="pb-1 font-normal text-right">{{ $t('DASHBOARDS.CLIENTS.COL_CALLED') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="g in groups" :key="g.key" class="border-t border-n-weak">
            <td class="py-2 text-n-slate-12">{{ $t(`DASHBOARDS.CLIENTS.GROUP_${g.key.toUpperCase()}`) }}</td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">{{ fmt(g.asked) }}</td>
            <td class="py-2 text-right tabular-nums text-n-slate-12">{{ fmt(g.answered) }}</td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">{{ fmtShare(g.answered_share) }}</td>
            <td class="py-2 text-right tabular-nums text-n-slate-12">{{ fmtAvg(g.avg) }}</td>
            <td class="py-2 text-right tabular-nums" :class="csiTone(g.csi)">
              {{ fmt(g.csi) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">{{ fmtShare(g.good_share) }}</td>
            <td class="py-2 text-right tabular-nums" :class="g.called_operator ? 'text-n-amber-11' : 'text-n-slate-11'">
              {{ fmt(g.called_operator) }}
            </td>
          </tr>
        </tbody>
      </table>
      <span class="text-xs text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.GROUPS_NOTE') }}</span>
    </section>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.CLIENTS.ANSWERED_BY_DAY') }}</h2>
        <DayChart :points="answeredPoints" kind="bars" :color="COLOR.teal" />
      </section>
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.CLIENTS.BOT_BY_DAY') }}</h2>
        <DayChart :points="botPoints" :color="COLOR.blue" :min="1" :max="5" :format="fmtAvg" />
      </section>
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.CLIENTS.OPERATOR_BY_DAY') }}</h2>
        <DayChart :points="operatorPoints" :color="COLOR.amber" :min="1" :max="5" :format="fmtAvg" />
      </section>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.CLIENTS.HIST') }}</h2>
        <div v-for="h in histogram" :key="h.mark" class="flex items-center gap-2 text-sm">
          <span class="w-3 text-n-slate-11 shrink-0">{{ h.mark }}</span>
          <div class="flex-1 h-5 rounded bg-n-alpha-1">
            <div class="h-5 rounded" :style="{ width: `${(h.count * 100) / histMax}%`, background: markColor(h.mark) }" />
          </div>
          <span class="w-8 text-right tabular-nums text-n-slate-11 shrink-0">{{ h.count }}</span>
        </div>
      </section>

      <section class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ $t('DASHBOARDS.CLIENTS.REASONS') }}</h2>
        <p v-if="!reasons.length" class="text-sm text-n-slate-11">{{ $t('DASHBOARDS.CLIENTS.NO_REASONS') }}</p>
        <div v-for="[reason, count] in reasons" :key="reason" class="flex flex-col gap-1 py-1 text-sm">
          <div class="flex justify-between gap-3">
            <span class="text-n-slate-12">{{ reason }}</span>
            <span class="tabular-nums text-n-slate-11 shrink-0">{{ count }}</span>
          </div>
          <div class="h-2 rounded-full bg-n-alpha-1">
            <div class="h-2 rounded-full" :style="{ width: `${pct(count, reasonsTotal)}%`, background: COLOR.ruby }" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
